import { Grid } from "./s25.virtual.grid";
import { S25Util } from "../../util/s25-util";
import { DowGrid } from "./s25.dow.grid.component";
import { StandardSchedule } from "../../services/standard.schedule.service";
import { computed, Signal, signal } from "@angular/core";
import { Nullable } from "../../pojo/Util";
import { S25Number } from "../../util/core/s25.number";

export namespace DowGridUtil {
    export function timesToPosition(times: DowGrid.Times, dowColumnIndex: ReadonlyMap<string, number>) {
        const columnCount = dowColumnIndex.size * 24;
        const dowIndex = dowColumnIndex.get(times.dow) ?? 0;
        if (!columnCount) return { left: 0, width: 0 };
        // DOW index + hour index = column index
        const left = ((dowIndex + times.startHour) / columnCount) * 100;
        let hours = times.endHour - times.startHour;
        if (hours < 0) hours += 24; // Item spans midnight
        const width = (hours / columnCount) * 100;

        return { left, width };
    }

    export function positionToTimes(item: { left: number; width: number }, dows: readonly string[]) {
        const columnCount = 24 * dows.length;

        const column = (item.left / 100) * columnCount;
        const dowIndex = S25Util.clamp(Math.floor(column / 24), 0, dows.length - 1);
        const dow = dows[dowIndex];
        // Figure out new start time, based on left and dow, aligned to minutes
        const startHour = Math.round((column % 24) * 60) / 60;
        // Figure out new end time, based on start and width
        let endHour = (startHour + (item.width / 100) * columnCount) % 24;
        if (endHour <= startHour) endHour += 24;
        endHour = Math.round(endHour * 60) / 60;

        return { dow, startHour, endHour };
    }

    export function getHeaders(
        dows: readonly string[],
        is24Hours: boolean,
        spanHours: number,
        visibility: {
            visibleDows: Signal<string[]>;
            startHour: Signal<number>;
            endHour: Signal<number>;
        },
    ): DowGrid.ColumnType[] {
        const headers: DowGrid.ColumnType[] = [];
        for (const dow of dows) {
            const hourHeaders: DowGrid.ColumnType[] = [];
            for (let hour = 0; hour < 24; hour++) {
                const start = S25Util.date.toTimeStrFromHours(hour, is24Hours);
                const heading = hour % spanHours === 0 ? `${start}` : "";
                hourHeaders.push({
                    id: `${dow}-${hour}`,
                    heading,
                    hidden: computed(() => {
                        const startHour = visibility.startHour();
                        const endHour = visibility.endHour();
                        return hour < startHour || hour >= endHour;
                    }),
                    dowGrid: { dow, hour, tick: !!heading },
                });
            }
            headers.push({
                id: dow,
                heading: dow,
                subHeaders: hourHeaders,
                hidden: computed(() => !visibility.visibleDows().includes(dow)),
                truncateOverflow: true,
                dowGrid: { dow, tick: true },
            });
        }

        return headers;
    }

    export function intersectingMeetingPatterns(itemDow: string, meetingPatterns: readonly string[]): string[] {
        const days = new Set(itemDow.split(""));
        return meetingPatterns.filter((pattern) => {
            if (pattern === itemDow) return false;
            return pattern.split("").some((day) => days.has(day));
        });
    }

    /** One primary projection. `placement` is the grid rectangle the consumer resolved, including row. */
    export function primaryProjection<ItemType extends DowGrid.SourceItem>(
        item: DowGrid.Item<ItemType>,
        options: {
            placement: Signal<DowGrid.Times & { top: number; height: number }>;
            is24Hours: Signal<boolean>;
        },
    ): DowGrid.ItemProjectionDefinition[] {
        const placement = options.placement;
        return [
            {
                key: item.id,
                role: "primary",
                data: {
                    dow: computed(() => placement().dow),
                    startHour: computed(() => placement().startHour),
                    endHour: computed(() => placement().endHour),
                    top: computed(() => placement().top),
                    height: computed(() => placement().height),
                },
                ariaLabel: computed(() => DowGridUtil.getAriaLabel(placement(), options.is24Hours())),
                hidden: computed(() => item.hidden?.() ?? false),
                noInteraction: computed(() => item.noInteraction?.() ?? false),
                minimapColor: computed(() => item.minimapColor?.()),
            },
        ];
    }

    export function shadowProjections<ProjectionData extends DowGrid.ProjectionData>(
        projection: DowGrid.ItemProjectionDefinition<ProjectionData>,
        options: {
            dows: readonly string[];
            is24Hours: boolean;
        },
    ): DowGrid.ItemProjectionDefinition<ProjectionData>[] {
        return DowGridUtil.intersectingMeetingPatterns(projection.data.dow(), options.dows).map((shadowDow) => {
            const times = computed(() => ({
                dow: shadowDow,
                startHour: projection.data.startHour(),
                endHour: projection.data.endHour(),
            }));
            return {
                ...projection,
                key: `${projection.key}-shadow-${shadowDow}`,
                role: "shadow",
                data: { ...projection.data, dow: signal(shadowDow) },
                ariaLabel: computed(() => DowGridUtil.getAriaLabel(times(), options.is24Hours)),
                draggable: computed(() => false),
                minimapColor: computed(() => "#111"),
            };
        });
    }

    export function proposeDragPositions<ItemType extends DowGrid.SourceItem>(options: {
        participants: readonly DowGrid.Item<ItemType>[];
        offset: Grid.Coord;
        dows: readonly string[];
        dowColumnIndex: ReadonlyMap<string, number>;
        resizeOnDowChange: boolean;
        schedule: Record<string, StandardSchedule[]> | undefined;
        snapToStandardSchedules: boolean;
    }): Map<DowGrid.Item<ItemType>, DowGrid.Times> {
        let byItem = new Map<DowGrid.Item<ItemType>, DowGrid.Times>();
        for (const participant of options.participants) {
            const committed = participant.position();
            const base = DowGridUtil.timesToPosition(committed, options.dowColumnIndex);
            const shifted = DowGridUtil.positionToTimes(
                { left: base.left + options.offset.x, width: base.width },
                options.dows,
            );
            byItem.set(participant, shifted);
        }

        if (options.resizeOnDowChange) {
            byItem = DowGridUtil.dowChangeResizer(byItem, options.dowColumnIndex);
        }
        if (options.snapToStandardSchedules && options.schedule) {
            byItem = DowGridUtil.scheduleSnapper(byItem, options.schedule, options.dowColumnIndex);
        }
        return DowGridUtil.clampDragPositions(byItem);
    }

    /** Adjusts duration when the day pattern changes. */
    export function dowChangeResizer<ItemType extends DowGrid.SourceItem>(
        items: ReadonlyMap<DowGrid.Item<ItemType>, DowGrid.Times>,
        dowColumnIndex: ReadonlyMap<string, number>,
    ): Map<DowGrid.Item<ItemType>, DowGrid.Times> {
        const resized = new Map(items);
        for (const [item, position] of items) {
            const committed = item.position();
            if (committed.dow.length === position.dow.length) continue;
            if (!dowColumnIndex.has(position.dow)) continue;

            const unResizableHours = item.dowGrid.unResizableHours?.() ?? 0;
            const resizableHours = position.endHour - position.startHour - unResizableHours;
            const resizedHours = resizableHours * (committed.dow.length / position.dow.length);
            const endHour = resizedHours + unResizableHours + position.startHour;
            resized.set(item, { ...position, endHour });
        }
        return resized;
    }

    /** Snaps every item or none of them. */
    export function scheduleSnapper<ItemType extends DowGrid.SourceItem>(
        items: ReadonlyMap<DowGrid.Item<ItemType>, DowGrid.Times>,
        schedulesByDow: Record<string, StandardSchedule[]>,
        dowColumnIndex: ReadonlyMap<string, number>,
    ): Map<DowGrid.Item<ItemType>, DowGrid.Times> {
        const snapped = new Map(items);
        for (const [item, position] of items) {
            if (!dowColumnIndex.has(position.dow)) return new Map(items);

            const startMinutes = position.startHour * 60;
            const endMinutes = position.endHour * 60;
            const searchStart = (position.startHour - (item.dowGrid.preHours?.() ?? 0)) * 60;
            const searchEnd = (position.endHour + (item.dowGrid.postHours?.() ?? 0)) * 60;

            const overlapping = (schedulesByDow[position.dow] || []).filter(
                (schedule) => searchStart <= schedule.end_time! && searchEnd >= schedule.start_time!,
            );
            let best: { overlap: number; schedule: StandardSchedule } | undefined;
            for (const schedule of overlapping) {
                const overlap = Math.min(
                    schedule.end_time! - startMinutes,
                    endMinutes - schedule.start_time!,
                    endMinutes - startMinutes,
                );
                if (!best || best.overlap < overlap) best = { overlap, schedule };
            }
            if (!best) return new Map(items);

            const startHour = best.schedule.start_time! / 60 - (item.dowGrid.preHours?.() ?? 0);
            const endHour = best.schedule.end_time! / 60 + (item.dowGrid.postHours?.() ?? 0);
            snapped.set(item, { ...position, dow: best.schedule.dow!, startHour, endHour });
        }
        return snapped;
    }

    export function clampDragPositions<ItemType extends DowGrid.SourceItem>(
        items: ReadonlyMap<DowGrid.Item<ItemType>, DowGrid.Times>,
    ): Map<DowGrid.Item<ItemType>, DowGrid.Times> {
        const clamped = new Map(items);
        for (const [item, position] of items) {
            const duration = position.endHour - position.startHour;
            const startHour = S25Number.clamp(position.startHour, 0, 24 - duration);
            const endHour = startHour + duration;
            clamped.set(item, { ...position, startHour, endHour });
        }
        return clamped;
    }

    export function fitsSchedule(options: {
        schedulesByDow: Record<string, StandardSchedule[]> | undefined;
        times: DowGrid.Times;
        preHours: Nullable<number>;
        postHours: Nullable<number>;
    }) {
        const { schedulesByDow, times, preHours, postHours } = options;
        if (!schedulesByDow) return true; // Nothing to conform to, so anything fits
        if (!times || times.startHour == null || times.endHour == null || !times.dow) return false;
        const startHour = times.startHour + (preHours ?? 0);
        const endHour = times.endHour - (postHours ?? 0);
        const [start, end] = [Math.round(startHour * 60), Math.round(endHour * 60)];
        const schedules = schedulesByDow[times.dow] || [];
        return schedules.some((matching) => matching.start_time === start && matching.end_time === end);
    }

    export function getStandardScheduleDecorations(
        standardSchedule: readonly StandardSchedule[] | undefined,
        options: {
            dowColumnIndex: ReadonlyMap<string, number>;
        },
    ): Grid.SourceDecoration<DowGrid.ScheduleDecorationData>[] {
        return (standardSchedule || []).map((schedule) => {
            const { id: blockId, dow, start_time: startTime, end_time: endTime } = schedule;
            const times = { dow: dow!, startHour: startTime! / 60, endHour: endTime! / 60 };
            const position = signal({
                ...DowGridUtil.timesToPosition(times, options.dowColumnIndex),
                top: -1,
                height: 102,
            });
            return {
                type: "decoration" as const,
                layer: "underlay" as const,
                key: `standard-schedule-${blockId}`,
                data: {
                    type: "standard-schedule" as const,
                    dow: dow!,
                    blockId: blockId!,
                },
                position,
                dragging: signal(false),
                moving: signal(false),
                broughtToFront: signal(false),
                hidden: signal(false),
                noInteraction: signal(true),
                ariaLabel: signal("Standard schedule"),
                minimapColor: signal("rgba(128,197,255,0.15)"),
            } satisfies Grid.SourceDecoration<DowGrid.ScheduleDecorationData>;
        });
    }

    export function formatAriaLabel(dow: string, startHour: number, endHour: number, is24Hours: boolean) {
        const startTime = S25Util.date.ariaTimeString(S25Util.date.toTimeStrFromHours(startHour, is24Hours));
        const endTime = S25Util.date.ariaTimeString(S25Util.date.toTimeStrFromHours(endHour, is24Hours));
        return `${dow}, ${startTime} to ${endTime}`;
    }

    export function getAriaLabel(times: DowGrid.Times, is24Hours: boolean) {
        return DowGridUtil.formatAriaLabel(times.dow, times.startHour, times.endHour, is24Hours);
    }
}
